import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { CheckCircle2, ThumbsUp } from 'lucide-react';
import StarRating from './StarRating';

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const FilterGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const FilterBtn = styled.button`
  font-size: ${({ theme }) => theme.type.bodyXs};
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-family: ${({ theme }) => theme.fonts.sans};
  padding: 0.35rem 0.75rem;
  border: 1px solid
    ${({ theme, $active }) => ($active ? theme.colors.foreground : theme.colors.border)};
  background: ${({ theme, $active }) =>
    $active ? theme.colors.foreground : 'transparent'};
  color: ${({ theme, $active }) => ($active ? theme.colors.cream : theme.colors.foreground)};
  cursor: pointer;
  &:hover {
    border-color: ${({ theme }) => theme.colors.foreground};
  }
`;

const SortSelect = styled.select`
  font-size: ${({ theme }) => theme.type.bodySm};
  font-family: ${({ theme }) => theme.fonts.sans};
  border: 1px solid ${({ theme }) => theme.colors.border};
  padding: 0.5rem 0.75rem;
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.foreground};
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.foreground};
  }
`;

const Empty = styled.p`
  font-size: ${({ theme }) => theme.type.bodySm};
  color: ${({ theme }) => theme.colors.mutedForeground};
  text-align: center;
  padding: 3rem 0;
`;

const Article = styled.article`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  padding-bottom: 2rem;
  margin-bottom: 2rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
  }
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 200px 1fr;
  }
`;

const MetaCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const Author = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.type.bodySm};
  font-family: ${({ theme }) => theme.fonts.sans};
  font-weight: 500;
`;

const Mut = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.type.bodyXs};
  color: ${({ theme }) => theme.colors.mutedForeground};
`;

const Verified = styled.p`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin: 0.5rem 0 0;
  font-size: ${({ theme }) => theme.type.bodyXs};
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: hsla(25, 8%, 10%, 0.72);
`;

const ReviewTitle = styled.h4`
  margin: 0 0 0.35rem;
  font-size: ${({ theme }) => theme.type.bodySm};
  font-weight: 500;
`;

const BodyText = styled.p`
  margin: 0 0 1rem;
  font-size: ${({ theme }) => theme.type.bodySm};
  line-height: 1.55;
  color: hsla(25, 8%, 12%, 0.85);
`;

const MetaLine = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 1.25rem;
  font-size: ${({ theme }) => theme.type.bodyXs};
  color: ${({ theme }) => theme.colors.mutedForeground};
  margin-bottom: 0.75rem;
`;

const Upper = styled.span`
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-right: 0.25rem;
`;

const Helpful = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: ${({ theme }) => theme.type.bodyXs};
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.mutedForeground};
`;

const LoadMoreWrap = styled.div`
  text-align: center;
  padding-top: 1rem;
`;

const LoadMoreBtn = styled.button`
  padding: 0.65rem 1.25rem;
  font-size: ${({ theme }) => theme.type.bodyXs};
  letter-spacing: 0.1em;
  text-transform: uppercase;
  border: 1px solid ${({ theme }) => theme.colors.foreground};
  background: transparent;
  color: ${({ theme }) => theme.colors.foreground};
  cursor: pointer;
  &:hover {
    background: ${({ theme }) => theme.colors.foreground};
    color: ${({ theme }) => theme.colors.cream};
  }
`;

function parseDate(d) {
  if (!d) return 0;
  const t = new Date(d).getTime();
  return Number.isNaN(t) ? 0 : t;
}

export default function ReviewList({ reviews = [], pageSize = 5 }) {
  const { t } = useTranslation();
  const [sort, setSort] = useState('recent');
  const [filterStars, setFilterStars] = useState(null);
  const [visible, setVisible] = useState(pageSize);

  const sorted = useMemo(() => {
    const arr = [...(reviews || [])];
    let base =
      filterStars != null ? arr.filter((r) => Number(r.rating) === filterStars) : arr;
    base = [...base];
    switch (sort) {
      case 'helpful':
        base.sort((a, b) => Number(b.helpful || 0) - Number(a.helpful || 0));
        break;
      case 'highest':
        base.sort((a, b) => Number(b.rating) - Number(a.rating));
        break;
      case 'lowest':
        base.sort((a, b) => Number(a.rating) - Number(b.rating));
        break;
      default:
        base.sort((a, b) => parseDate(b.date) - parseDate(a.date));
    }
    return base;
  }, [reviews, sort, filterStars]);

  return (
    <div style={{ marginTop: '2rem' }}>
      <Toolbar>
        <FilterGroup>
          <FilterBtn type="button" $active={filterStars === null} onClick={() => setFilterStars(null)}>
            {t('modamai.reviews.filterAll')}
          </FilterBtn>
          {[5, 4, 3, 2, 1].map((s) => (
            <FilterBtn
              key={s}
              type="button"
              $active={filterStars === s}
              onClick={() => setFilterStars(s)}
            >
              {s} ★
            </FilterBtn>
          ))}
        </FilterGroup>
        <SortSelect
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          aria-label={t('modamai.reviews.sortAria')}
        >
          <option value="recent">{t('modamai.reviews.sortRecent')}</option>
          <option value="helpful">{t('modamai.reviews.sortHelpful')}</option>
          <option value="highest">{t('modamai.reviews.sortHighest')}</option>
          <option value="lowest">{t('modamai.reviews.sortLowest')}</option>
        </SortSelect>
      </Toolbar>

      {sorted.length === 0 ? <Empty>{t('modamai.reviews.noMatchFilter')}</Empty> : null}

      <div style={{ marginTop: '2rem' }}>
        {sorted.slice(0, visible).map((r) => (
          <Article key={r.id}>
            <MetaCol>
              <Author>{r.author}</Author>
              {r.location ? <Mut>{r.location}</Mut> : null}
              {r.verified ? (
                <Verified>
                  <CheckCircle2 size={14} aria-hidden /> {t('modamai.reviews.verifiedBuyer')}
                </Verified>
              ) : null}
              <Mut style={{ paddingTop: '0.35rem' }}>
                {parseDate(r.date)
                  ? new Date(r.date).toLocaleDateString(undefined, {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                    })
                  : '—'}
              </Mut>
            </MetaCol>
            <div>
              <StarRating rating={Number(r.rating)} size="md" />
              {r.title ? <ReviewTitle>{r.title}</ReviewTitle> : null}
              {r.body ? <BodyText>{r.body}</BodyText> : null}
              {r.size || r.fit || r.recommends ? (
                <MetaLine>
                  {r.size ? (
                    <span>
                      <Upper>{t('modamai.reviews.metaSize')}</Upper>
                      {r.size}
                    </span>
                  ) : null}
                  {r.fit ? (
                    <span>
                      <Upper>{t('modamai.reviews.metaFit')}</Upper>
                      {r.fit}
                    </span>
                  ) : null}
                  {r.recommends ? (
                    <span style={{ color: 'hsla(20, 10%, 10%, 0.72)' }}>
                      {t('modamai.reviews.wouldRecommendLine')}
                    </span>
                  ) : null}
                </MetaLine>
              ) : null}
              <Helpful>
                <ThumbsUp size={14} aria-hidden /> {t('modamai.reviews.helpful', { count: r.helpful ?? 0 })}
              </Helpful>
            </div>
          </Article>
        ))}
      </div>

      {visible < sorted.length ? (
        <LoadMoreWrap>
          <LoadMoreBtn type="button" onClick={() => setVisible((v) => v + pageSize)}>
            {t('modamai.reviews.loadMore')}
          </LoadMoreBtn>
        </LoadMoreWrap>
      ) : null}
    </div>
  );
}
